import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { dateLocaleTag } from '@/lib/date-locale';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import type { TimeseriesItem } from '@/types/api';

interface ActivationsChartProps {
  data: TimeseriesItem[] | undefined;
  total: number | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function formatDay(value: string, tag: string): string {
  const d = new Date(value);
  return d.toLocaleDateString(tag, { day: 'numeric', month: 'short' });
}

interface ChartTooltipPayloadItem {
  value?: number;
}

function ChartTooltip({
  active,
  payload,
  label,
  tag,
}: {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
  label?: string;
  tag: string;
}) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0 || !label) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 shadow-md">
      <p className="text-[11.5px] text-faint">{formatDay(label, tag)}</p>
      <p className="mt-0.5 font-mono text-[13px] font-medium tabular-nums text-popover-foreground">
        {payload[0]?.value}
        <span className="ml-1.5 font-sans text-[11.5px] font-normal text-muted-foreground">
          {t('overview.chart.tooltipActivations', { count: payload[0]?.value ?? 0 })}
        </span>
      </p>
    </div>
  );
}

function ChartHeader({
  data,
  total,
  tag,
}: {
  data?: TimeseriesItem[];
  total?: number | undefined;
  tag: string;
}) {
  const { t } = useTranslation();
  const first = data?.[0]?.date;
  const last = data?.[data.length - 1]?.date;
  return (
    <div className="mb-5 flex items-baseline justify-between gap-4">
      <h2 className="text-xl font-semibold tracking-tight text-foreground">
        {t('overview.chart.title')}
      </h2>
      {first && last && (
        <span className="text-[13.5px] text-faint">
          {formatDay(first, tag)} – {formatDay(last, tag)}
          {total !== undefined && ` · ${t('overview.chart.total', { count: total })}`}
        </span>
      )}
    </div>
  );
}

export function ActivationsChart({
  data,
  total,
  isLoading,
  isError,
  onRetry,
}: ActivationsChartProps) {
  const { t, i18n } = useTranslation();
  const tag = dateLocaleTag(i18n.language);

  if (isError) {
    return (
      <div>
        <ChartHeader tag={tag} />
        <p className="text-sm text-destructive">{t('overview.chart.error')}</p>
        <button onClick={onRetry} className="mt-2 text-sm text-primary underline">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <ChartHeader tag={tag} />
        <Skeleton className="h-[280px] w-full" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div>
        <ChartHeader tag={tag} />
        <Skeleton className="h-[280px] w-full" />
        <p className="mt-3 text-sm text-muted-foreground">{t('overview.chart.empty')}</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.activations), 1);
  const midValue = Math.round(maxValue / 2);
  const yTicks = [0, midValue, maxValue];
  const tickIndices = [0, Math.floor((data.length - 1) / 2), data.length - 1];
  const xLabels = tickIndices.map((i) => data[i]?.date).filter((d): d is string => d !== undefined);
  const lastIndex = data.length - 1;

  return (
    <div data-testid="activations-chart">
      <ChartHeader data={data} total={total} tag={tag} />
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 18, left: 18, bottom: 0 }}>
            <defs>
              <linearGradient id="activationsAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity={0.14} />
                <stop offset="65%" stopColor="hsl(var(--foreground))" stopOpacity={0.04} />
                <stop offset="100%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="hsl(var(--foreground))" strokeOpacity={0.07} />
            <XAxis dataKey="date" tick={false} tickLine={false} axisLine={false} height={10} />
            <YAxis
              orientation="right"
              domain={[0, maxValue]}
              ticks={yTicks}
              tick={{
                fontSize: 11,
                fill: 'hsl(var(--faint))',
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
              tickLine={false}
              axisLine={false}
              width={32}
              allowDecimals={false}
            />
            <RechartsTooltip
              cursor={{
                stroke: 'hsl(var(--foreground))',
                strokeOpacity: 0.22,
                strokeWidth: 1,
                strokeDasharray: '3 3',
              }}
              content={<ChartTooltip tag={tag} />}
            />
            <Area
              type="monotone"
              dataKey="activations"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              fill="url(#activationsAreaFill)"
              activeDot={{
                r: 4.5,
                fill: 'hsl(var(--primary))',
                stroke: 'hsl(var(--background))',
                strokeWidth: 2,
              }}
              dot={(props: { cx?: number; cy?: number; index?: number; key?: string | number }) => {
                const { cx, cy, index, key } = props;
                if (index !== lastIndex || cx === undefined || cy === undefined) {
                  return <g key={key} />;
                }
                return (
                  <g key={key}>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={9}
                      fill="none"
                      stroke="hsl(var(--primary) / 0.35)"
                      strokeWidth={1.5}
                    />
                    <circle cx={cx} cy={cy} r={4.5} fill="hsl(var(--primary))" />
                  </g>
                );
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
        {xLabels.length === 3 && (
          <div
            className="flex justify-between text-xs text-faint"
            style={{ paddingLeft: 18, paddingRight: 50 }}
          >
            <span>{formatDay(xLabels[0] ?? '', tag)}</span>
            <span className="text-center">{formatDay(xLabels[1] ?? '', tag)}</span>
            <span className="text-right">{formatDay(xLabels[2] ?? '', tag)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
